"use client";

import { useEffect, useId, useRef, useState } from "react";
import Script from "next/script";
import { CheckCircleIcon, ChevronDownIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { TrackingEvents } from "@/lib/analytics";
import { useFormTracking } from "@/hooks/useFormTracking";
import {
  buildBrowserAttributionEnvelope,
  createSubmissionId,
  setBrowserAttributionLeadId,
} from "@/lib/ad-attribution";
import { useTurnstile } from "@/hooks/useTurnstile";
import { FieldLabel, InlineError, fieldClassName } from "@/components/shared/DesignSystem";
import { suppressDiagnosticNudge } from "@/lib/diagnostic-nudge";
import { focusFirstFormError } from "@/components/shared/formValidationFocus";
import { submitCrmForm } from "@/lib/form-submission";
import { getAssessmentEmailError, getHomeAssessmentSuccessMessage } from "@/lib/assessment-contact";

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || "pk.eyJ1IjoidGVkY2FvY29tbyIsImEiOiJjbTgxeW0wOGYwbWdsMmlwenVpY2x0d244In0.0EI4_0iJRISomZ78wOOS5Q";

const WATER_ISSUES = [
  ["hard-water", "Hard water or scale"],
  ["bad-taste", "Unpleasant taste"],
  ["unpleasant-odor", "Unpleasant odour"],
  ["staining-issues", "Staining or discolouration"],
  ["low-pressure", "Low water pressure"],
  ["sediment", "Sediment or particles"],
  ["not-sure", "Not sure yet"],
];

const PROPERTY_TYPES = [
  ["", "Select property type"],
  ["detached-house", "Detached house"],
  ["townhouse", "Townhouse"],
  ["condominium", "Condominium"],
  ["apartment", "Apartment"],
  ["bungalow", "Bungalow"],
  ["residential", "Other residential property"],
];

function createInitialData(initialWaterIssue) {
  return {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    preferredContactMethod: "",
    propertyType: "",
    householdSize: "",
    waterSource: "",
    hasFinishedBasement: "",
    waterMeterInEquipmentRoom: "",
    waterIssues: initialWaterIssue ? [initialWaterIssue] : [],
  };
}

export function HomeAssessmentForm({ programInterest = null, initialWaterIssue = null }) {
  const id = useId();
  const formRef = useRef(null);
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const addressTimerRef = useRef(null);
  const addressAbortRef = useRef(null);
  const submissionIdRef = useRef(null);
  const submittingRef = useRef(false);
  const trackedDetailsRef = useRef(false);
  const trackedLegacyStartRef = useRef(false);
  const [formData, setFormData] = useState(() => createInitialData(initialWaterIssue));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const { turnstileRef, turnstileToken, resetTurnstile, handleScriptLoad } = useTurnstile({ responsive: true });
  const formVariant = initialWaterIssue ? `home-assessment-${initialWaterIssue}-intent` : "home-assessment-general";
  const trackingDimensions = {
    form_type: "home_assessment",
    form_variant: formVariant,
    program_interest: programInterest || "general",
    placement: "inline",
    cta_variant: initialWaterIssue ? `${initialWaterIssue}-assessment` : "home-assessment",
    page_path: "/forms/home-assessment",
  };
  const trackFormStart = useFormTracking({ formRef, dimensions: trackingDimensions });

  const turnstileError = turnstileToken ? null : errors.turnstile;
  const requiresEmail = formData.preferredContactMethod === "email";

  useEffect(() => {
    function handleOutside(event) {
      if (!suggestionsRef.current?.contains(event.target) && !addressInputRef.current?.contains(event.target)) {
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      }
    }
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, []);

  useEffect(() => () => {
    clearTimeout(addressTimerRef.current);
    addressAbortRef.current?.abort();
  }, []);

  function clearFieldError(name) {
    if (!errors[name]) return;
    setErrors((current) => {
      const next = { ...current };
      delete next[name];
      return next;
    });
  }

  function handleChange(event) {
    const { name, value } = event.target;
    clearFieldError(name);
    if (name === "preferredContactMethod" && errors.email) {
      setErrors((current) => {
        const next = { ...current };
        const emailError = getAssessmentEmailError(formData.email, value);
        if (emailError) next.email = emailError;
        else delete next.email;
        return next;
      });
    }
    setFormData((current) => ({ ...current, [name]: value }));
  }

  function toggleWaterIssue(issue) {
    setFormData((current) => ({
      ...current,
      waterIssues: current.waterIssues.includes(issue)
        ? current.waterIssues.filter((item) => item !== issue)
        : [...current.waterIssues, issue],
    }));
  }

  function selectSuggestion(suggestion) {
    const address = suggestion.place_name || suggestion.properties?.full_address || suggestion.text || "";
    setFormData((current) => ({ ...current, address }));
    setShowSuggestions(false);
    setActiveSuggestion(-1);
    requestAnimationFrame(() => addressInputRef.current?.focus());
  }

  function handleAddressKeyDown(event) {
    if (!showSuggestions || suggestions.length === 0) {
      if (event.key === "Escape") {
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestion((current) => (current + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestion((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
    } else if (event.key === "Enter" && activeSuggestion >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeSuggestion]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }
  }

  function handleAddressChange(event) {
    const value = event.target.value;
    clearFieldError("address");
    setFormData((current) => ({ ...current, address: value }));
    setActiveSuggestion(-1);
    clearTimeout(addressTimerRef.current);
    addressAbortRef.current?.abort();

    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
      return;
    }

    setShowSuggestions(true);
    setIsLoadingSuggestions(true);
    addressTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      addressAbortRef.current = controller;
      try {
        const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value.trim())}.json?access_token=${MAPBOX_TOKEN}&country=ca&limit=5&types=address,place,postcode`;
        const response = await fetch(endpoint, { signal: controller.signal });
        if (!response.ok) throw new Error(`Address lookup failed with ${response.status}`);
        const data = await response.json();
        setSuggestions(Array.isArray(data.features) ? data.features : []);
      } catch (error) {
        if (error?.name !== "AbortError") setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setIsLoadingSuggestions(false);
      }
    }, 300);
  }

  function validate() {
    const nextErrors = {};
    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    const digits = formData.phone.replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 15) nextErrors.phone = "Enter a valid phone number with 10 to 15 digits.";
    const emailError = getAssessmentEmailError(formData.email, formData.preferredContactMethod);
    if (emailError) nextErrors.email = emailError;
    if (formData.householdSize && (Number(formData.householdSize) < 1 || Number(formData.householdSize) > 30)) {
      nextErrors.householdSize = "Enter a household size between 1 and 30.";
    }
    if (!turnstileToken) nextErrors.turnstile = "Complete the human verification check.";
    return nextErrors;
  }

  function trackStart() {
    suppressDiagnosticNudge(window);
    trackFormStart();
    if (!trackedLegacyStartRef.current) {
      trackedLegacyStartRef.current = true;
      TrackingEvents.trackHomeAssessmentStep("start", trackingDimensions);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submittingRef.current) return;
    trackStart();

    const nextErrors = validate();
    TrackingEvents.trackFormStep("submit_attempt", {
      ...trackingDimensions,
      water_issue_count: formData.waterIssues.length,
    });
    TrackingEvents.trackHomeAssessmentStep("submit_attempt", trackingDimensions);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus({ type: "error", message: "Please check the highlighted fields and try again." });
      TrackingEvents.trackFormStep("validation_error", {
        ...trackingDimensions,
        error_fields: Object.keys(nextErrors).join(","),
        error_count: Object.keys(nextErrors).length,
      });
      TrackingEvents.trackHomeAssessmentStep("validation_error", trackingDimensions);
      requestAnimationFrame(() => focusFirstFormError(formRef.current));
      return;
    }

    submittingRef.current = true;
    const submittedContactMethod = formData.preferredContactMethod;
    try {
      setIsSubmitting(true);
      setStatus({ type: "idle", message: "" });
      submissionIdRef.current ||= createSubmissionId();
      const attribution = buildBrowserAttributionEnvelope({
        submissionId: submissionIdRef.current,
        formType: "home_assessment",
      });

      const { response, result } = await submitCrmForm("/api/crm/submit-assessment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "new",
          type: "home-assessment",
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          property_type: formData.propertyType,
          household_size: formData.householdSize || null,
          water_source: formData.waterSource ? [formData.waterSource === "city" ? "tap-water" : "well-water"] : [],
          finished_basement: formData.hasFinishedBasement ? formData.hasFinishedBasement === "yes" : null,
          water_meter_in_equipment_room: formData.waterMeterInEquipmentRoom ? formData.waterMeterInEquipmentRoom === "yes" : null,
          water_issues: formData.waterIssues,
          preferred_contact_method: formData.preferredContactMethod,
          program_interest: programInterest || "residential",
          source: attribution?.submissionTouch?.source,
          attribution,
          consultation_context: {
            mode: "residential",
            audience: "homeowner",
            service: programInterest || (initialWaterIssue === "hard-water" ? "water-softener" : "residential"),
            pagePath: "/forms/home-assessment",
            placement: "inline",
            ctaVariant: initialWaterIssue ? `${initialWaterIssue}-assessment` : "home-assessment",
          },
          turnstile_token: turnstileToken,
        }),
      });
      if (!response.ok || !result.success) {
        if (result.fieldErrors) {
          setErrors((current) => ({
            ...current,
            ...(result.fieldErrors.first_name ? { firstName: result.fieldErrors.first_name } : {}),
            ...(result.fieldErrors.phone ? { phone: result.fieldErrors.phone } : {}),
            ...(result.fieldErrors.email ? { email: result.fieldErrors.email } : {}),
            ...(result.fieldErrors.household_size ? { householdSize: result.fieldErrors.household_size } : {}),
          }));
          requestAnimationFrame(() => focusFirstFormError(formRef.current));
        }
        const submissionError = new Error(result.error || "Your assessment could not be submitted.");
        submissionError.code = result.code;
        throw submissionError;
      }

      const leadId = result.data?.leadId || result.data?.project?.projectId || result.data?.project?.id;
      if (!leadId) throw new Error("Lead creation was not confirmed.");
      try { setBrowserAttributionLeadId(leadId); }
      catch (storageError) { console.error("Confirmed lead storage failed:", storageError); }
      try {
        TrackingEvents.trackHomeAssessment({
          formId: leadId,
          programInterest: programInterest || "residential",
          updated: result.updated || false,
          form_variant: formVariant,
          placement: "inline",
          cta_variant: trackingDimensions.cta_variant,
          page_path: "/forms/home-assessment",
        });
      } catch (trackingError) {
        console.error("Confirmed lead tracking failed:", trackingError);
      }
      submissionIdRef.current = null;
      setFormData(createInitialData(initialWaterIssue));
      setErrors({});
      setStatus({
        type: "success",
        message: getHomeAssessmentSuccessMessage(submittedContactMethod),
      });
      resetTurnstile();
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.code === "submission_unconfirmed"
          ? error.message
          : `${error instanceof Error ? error.message : "We could not submit your request."} Try again, or call 289-748-9666.`,
      });
      resetTurnstile();
      try {
        TrackingEvents.trackFormStep("submit_error", {
          ...trackingDimensions,
          error_type: error instanceof Error ? "submission_failed" : "unknown",
        });
        TrackingEvents.trackHomeAssessmentStep("submit_error", trackingDimensions);
      } catch (trackingError) {
        console.error("Submission error tracking failed:", trackingError);
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  if (status.type === "success") {
    return (
      <div className="rounded-[1.375rem] border border-emerald-200 bg-emerald-50 p-6 sm:p-8" role="status" aria-live="polite">
        <CheckCircleIcon className="h-9 w-9 text-water-teal" aria-hidden="true" />
        <h2 className="mt-4 text-2xl font-semibold text-water-ink">Assessment request received</h2>
        <p className="mt-3 max-w-2xl text-base leading-7 text-water-muted">{status.message}</p>
        <a href="tel:+12897489666" className="mt-5 inline-flex min-h-11 items-center gap-2 font-semibold text-water-blue hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2">
          <PhoneIcon className="h-5 w-5" aria-hidden="true" />
          Call 289-748-9666
        </a>
      </div>
    );
  }

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        async
        defer
        strategy="afterInteractive"
        onLoad={handleScriptLoad}
        onReady={handleScriptLoad}
      />
      <form ref={formRef} onSubmit={handleSubmit} onFocusCapture={trackStart} className="wd-form space-y-7" noValidate>
        <fieldset>
          <legend className="text-lg font-semibold text-water-ink">How should we reach you?</legend>
          <p className="mt-1 text-sm leading-6 text-water-muted">First name and phone are required. Email is required only if you choose email contact.</p>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor={`${id}-first-name`}>First name</FieldLabel>
              <input id={`${id}-first-name`} name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleChange} required aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? `${id}-first-name-error` : undefined} className={fieldClassName} />
              <InlineError id={`${id}-first-name-error`}>{errors.firstName}</InlineError>
            </div>
            <div>
              <FieldLabel htmlFor={`${id}-phone`}>Phone</FieldLabel>
              <input id={`${id}-phone`} name="phone" type="tel" inputMode="tel" autoComplete="tel" value={formData.phone} onChange={handleChange} placeholder="289-748-9666" required aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? `${id}-phone-error` : undefined} className={fieldClassName} />
              <InlineError id={`${id}-phone-error`}>{errors.phone}</InlineError>
            </div>
          </div>
        </fieldset>

        <details className="group overflow-hidden rounded-[1rem] border border-water-border bg-water-canvas">
          <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 marker:content-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue">
            <span>
              <span className="block font-semibold text-water-ink">Add water concerns (optional)</span>
              <span className="mt-1 block text-sm text-water-muted">
                {formData.waterIssues.length > 0
                  ? `${formData.waterIssues.length} concern${formData.waterIssues.length === 1 ? "" : "s"} selected`
                  : "Choose any symptoms you notice"}
              </span>
            </span>
            <ChevronDownIcon className="h-5 w-5 shrink-0 text-water-blue transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
          </summary>
          <fieldset className="border-t border-water-border bg-white p-5">
            <legend className="sr-only">What are you noticing?</legend>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {WATER_ISSUES.map(([value, label]) => {
                const selected = formData.waterIssues.includes(value);
                return (
                  <label key={value} className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-[0.875rem] border px-4 py-3 text-sm font-medium transition-[border-color,background-color,box-shadow] focus-within:ring-2 focus-within:ring-water-blue focus-within:ring-offset-2 ${selected ? "border-water-blue bg-sky-50 text-water-ink shadow-sm" : "border-water-border bg-white text-water-muted hover:border-sky-300"}`}>
                    <input type="checkbox" value={value} checked={selected} onChange={() => toggleWaterIssue(value)} className="h-4 w-4 rounded border-water-border text-water-blue focus:ring-water-blue" />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </details>

        <details
          className="group overflow-hidden rounded-[1rem] border border-water-border bg-water-canvas"
          onToggle={(event) => {
            if (!event.currentTarget.open || trackedDetailsRef.current) return;
            trackedDetailsRef.current = true;
            TrackingEvents.trackHomeAssessmentStep("optional_details", { required_fields_completed: Boolean(formData.firstName.trim() && formData.phone.trim()) });
          }}
        >
          <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 marker:content-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue">
            <span>
              <span className="block font-semibold text-water-ink">{requiresEmail ? "Add home details (email required)" : "Add home details (optional)"}</span>
              <span className="mt-1 block text-sm text-water-muted">Email, service area, household, water source, and installation context</span>
            </span>
            <ChevronDownIcon className="h-5 w-5 shrink-0 text-water-blue transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
          </summary>

          <div className="space-y-6 border-t border-water-border bg-white p-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <FieldLabel htmlFor={`${id}-last-name`} optional>Last name</FieldLabel>
                <input id={`${id}-last-name`} name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleChange} className={fieldClassName} />
              </div>
              <div>
                <FieldLabel htmlFor={`${id}-email`} optional={!requiresEmail}>Email</FieldLabel>
                <input id={`${id}-email`} name="email" type="email" inputMode="email" autoComplete="email" spellCheck={false} required={requiresEmail} value={formData.email} onChange={handleChange} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? `${id}-email-error` : undefined} className={fieldClassName} />
                <InlineError id={`${id}-email-error`}>{errors.email}</InlineError>
              </div>
            </div>

            <div className="relative">
              <FieldLabel htmlFor={`${id}-address`} optional>City, postal code, or service address</FieldLabel>
              <input
                ref={addressInputRef}
                id={`${id}-address`}
                name="address"
                value={formData.address}
                onChange={handleAddressChange}
                onKeyDown={handleAddressKeyDown}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                autoComplete="street-address"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showSuggestions}
                aria-controls={`${id}-address-listbox`}
                aria-activedescendant={showSuggestions && activeSuggestion >= 0 ? `${id}-address-option-${activeSuggestion}` : undefined}
                className={fieldClassName}
              />
              <p className="mt-1 text-sm leading-6 text-water-muted">Used to confirm service area and installation context.</p>
              {showSuggestions ? (
                <div ref={suggestionsRef} className="absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-[0.875rem] border border-water-border bg-white p-1 shadow-xl">
                  {isLoadingSuggestions ? <p className="px-3 py-2 text-sm text-water-muted" role="status">Looking up addresses…</p> : null}
                  {!isLoadingSuggestions && suggestions.length === 0 ? <p className="px-3 py-2 text-sm text-water-muted">No suggestions. You can keep the address as typed.</p> : null}
                  <ul id={`${id}-address-listbox`} role="listbox">
                    {suggestions.map((suggestion, index) => (
                      <li
                        key={suggestion.id}
                        id={`${id}-address-option-${index}`}
                        role="option"
                        aria-selected={activeSuggestion === index}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectSuggestion(suggestion)}
                        className={`w-full cursor-pointer rounded-[0.625rem] px-3 py-2 text-left text-sm leading-6 text-water-ink ${activeSuggestion === index ? "bg-sky-50" : "hover:bg-water-canvas"}`}
                      >
                        {suggestion.place_name || suggestion.properties?.full_address || suggestion.text}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <FieldLabel htmlFor={`${id}-contact-method`} optional>Preferred contact</FieldLabel>
                <select id={`${id}-contact-method`} name="preferredContactMethod" value={formData.preferredContactMethod} onChange={handleChange} className={fieldClassName}>
                  <option value="">No preference</option>
                  <option value="phone">Phone call</option>
                  <option value="text">Text message</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div>
                <FieldLabel htmlFor={`${id}-property-type`} optional>Property type</FieldLabel>
                <select id={`${id}-property-type`} name="propertyType" value={formData.propertyType} onChange={handleChange} className={fieldClassName}>
                  {PROPERTY_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel htmlFor={`${id}-household-size`} optional>Household size</FieldLabel>
                <input id={`${id}-household-size`} name="householdSize" type="number" inputMode="numeric" min="1" max="30" value={formData.householdSize} onChange={handleChange} aria-invalid={Boolean(errors.householdSize)} aria-describedby={errors.householdSize ? `${id}-household-error` : undefined} className={fieldClassName} />
                <InlineError id={`${id}-household-error`}>{errors.householdSize}</InlineError>
              </div>
              <div>
                <FieldLabel htmlFor={`${id}-water-source`} optional>Water source</FieldLabel>
                <select id={`${id}-water-source`} name="waterSource" value={formData.waterSource} onChange={handleChange} className={fieldClassName}>
                  <option value="">Not sure</option>
                  <option value="city">Municipal water</option>
                  <option value="well">Private well</option>
                </select>
              </div>
              <div>
                <FieldLabel htmlFor={`${id}-basement`} optional>Finished basement?</FieldLabel>
                <select id={`${id}-basement`} name="hasFinishedBasement" value={formData.hasFinishedBasement} onChange={handleChange} className={fieldClassName}>
                  <option value="">Not provided</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              {formData.hasFinishedBasement === "yes" ? (
                <div>
                  <FieldLabel htmlFor={`${id}-meter`} optional>Water meter in the equipment room?</FieldLabel>
                  <select id={`${id}-meter`} name="waterMeterInEquipmentRoom" value={formData.waterMeterInEquipmentRoom} onChange={handleChange} className={fieldClassName}>
                    <option value="">Not sure</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              ) : null}
            </div>
          </div>
        </details>

        <div tabIndex={-1} aria-label="Human verification" aria-invalid={Boolean(turnstileError)} aria-describedby={turnstileError ? `${id}-turnstile-error` : undefined} className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2">
          <div ref={turnstileRef} className="min-h-[66px]" aria-label="Human verification" />
          <InlineError id={`${id}-turnstile-error`}>{turnstileError}</InlineError>
        </div>

        {status.type === "error" ? <p className="rounded-[0.875rem] border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800" role="alert">{status.message}</p> : null}

        <button type="submit" disabled={isSubmitting} className="inline-flex min-h-12 w-full items-center justify-center rounded-[0.875rem] border border-water-blue bg-water-blue px-6 py-3 text-base font-semibold text-white transition-[background-color,border-color] duration-200 hover:border-water-blue-dark hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70 sm:w-auto">
          {isSubmitting ? "Sending request…" : "Request My Free Assessment"}
        </button>
        <p className="text-sm leading-6 text-water-muted">No obligation. A specialist will confirm the next step before any visit, quote, or purchase.</p>
      </form>
    </>
  );
}
